import type { DeviceProfile, MidiCC } from "~/types/midi";

/**
 * Apply a device profile to a CC array
 *
 * Takes the profile's ccConfig and applies it to the device's CC state:
 * - Sets scramble enabled/disabled based on profile
 * - Sets show to match enabled state (enabled CCs are visible in UI)
 * - Sets min/max ranges from profile
 * - Preserves current values and smooth settings
 *
 * @param ccs - Current CC array (all 128 CCs)
 * @param profile - Device profile to apply
 * @returns Updated CC array with profile settings applied
 */
export function applyProfileToCCs(ccs: MidiCC[], profile: DeviceProfile): MidiCC[] {
  // Create a map of CC number → profile config for fast lookup
  const profileMap = new Map(
    profile.ccConfig.map(config => [config.number, config])
  );

  return ccs.map(cc => {
    const profileConfig = profileMap.get(cc.number);

    if (profileConfig) {
      // This CC has profile configuration - apply it
      return {
        ...cc,
        scramble: profileConfig.enabled,
        show: profileConfig.enabled, // Show enabled CCs in UI
        min: profileConfig.min,
        max: profileConfig.max,
        label: profileConfig.label, // Apply label from profile
      };
    } else {
      // This CC is not in the profile - disable scrambling, use full range
      return {
        ...cc,
        scramble: false,
        show: false, // Don't show CCs that aren't in the profile
        min: 0,
        max: 127,
        label: cc.label || `CC ${cc.number}`, // Keep existing label or use default
      };
    }
  });
}

/**
 * Get profile by ID from a profile array
 *
 * @param profiles - Array of device profiles
 * @param profileId - ID of the profile to find
 * @returns The matching profile, or undefined if not found
 */
export function getProfileById(
  profiles: DeviceProfile[],
  profileId: string
): DeviceProfile | undefined {
  return profiles.find(p => p.id === profileId);
}

/**
 * Get the first profile from an array, or undefined if empty
 *
 * @param profiles - Array of device profiles
 * @returns The first profile, or undefined if array is empty
 */
export function getFirstProfile(profiles: DeviceProfile[]): DeviceProfile | undefined {
  return profiles[0];
}
